import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// PACKAGES
import { useParams } from 'react-router-dom';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import ScrollToTopButton from '../../components/ScrollToTopButton';
import ProfileSummary from '../../components/ProfileSummary';
import ProfileCertifications from '../../components/ProfileCertifications';
import ProfileOrganisations from '../../components/ProfileOrganisations';
import ProfileComplaints from '../../components/ProfileComplaints';
import ProfileFeedback from '../../components/ProfileFeedback';
import ProfileIncidents from '../../components/ProfileIncidents';
import ProfilePolicies from '../../components/ProfilePolicies';
import ProfileChecklists from '../../components/ProfileChecklists';
import ProfileTraining from '../../components/ProfileTraining';
import LoadingSpinner from '../../components/LoadingSpinner';

// TYPES
import { RouteParams } from '../../Interfaces';
import { StaffPastStaffMember } from '../../api/models';
import DisplayDate from '../../components/DisplayDate';
import { useAppSelector } from '../../store';
import StatusIcon from '../../components/StatusIcon';
import Button from '../../components/Button';
import PastProfileCertifications from '../../components/PastProfileCertifications';

const PastStaffProfile: React.FC = () => {
	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// Set up Data state and Initial Data
	const initialStaffPastProfileData: StaffPastStaffMember = {
		id: undefined,
		childAwareId: undefined,
		firstName: undefined,
		lastName: undefined,
		email: undefined,
		profileImage: undefined,
		staffType: undefined,
		isCurrentStaff: undefined,
		isOrganisationAdmin: undefined,
		phoneNumber: undefined,
		dateOfBirth: undefined,
		certifications: [],
		organisations: [],
		complaints: [],
		feedback: [],
		incidents: [],
		policies: [],
		overdueChecklists: [],
		completedChecklists: [],
		trainings: [],
		isYou: undefined,
		canUpdate: undefined
	};

	const [staffPastProfileData, setStaffPastProfileData] =
		React.useState<StaffPastStaffMember>(initialStaffPastProfileData);

	// Get the id from params
	const params = useParams<RouteParams>();
	const staffId = params.id;

	// Get all Staff Types from Redux store
	const staffTypes = useAppSelector((state) => state.lists.staffTypes);

	// Checklist pagination and load-more loading state
	const [overdueChecklistPage, setOverdueChecklistPage] = React.useState<number>(1);
	const [completedChecklistPage, setCompletedChecklistPage] = React.useState<number>(1);

	const [loadingMoreOverdueChecklist, setLoadingMoreOverdueChecklist] = React.useState<boolean>(false);
	const [loadingMoreCompletedChecklist, setLoadingMoreCompletedChecklist] = React.useState<boolean>(false);

	// Function to  Get staff profile data from api call
	const getStaffPastProfileData = async (overduePage?: number, completedPage?: number) => {
		setIsLoading(true);

		const response = await apibridge.getStaffPast(staffId, overduePage ?? 1, completedPage ?? 1);

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			const { result = initialStaffPastProfileData } = response;

			setIsLoading(false);

			setStaffPastProfileData(result);

			if (overduePage === 1 && completedPage === 1) {
				setOverdueChecklistPage(1);
				setCompletedChecklistPage(1);
			}
		}
	};

	const loadMoreOverdueChecklists = async () => {
		setLoadingMoreOverdueChecklist(true);

		const nextPage = overdueChecklistPage + 1;

		const response = await apibridge.getStaffPast(staffId, nextPage, completedChecklistPage);

		if (!response.isError && response.result) {
			setStaffPastProfileData((prev) => ({
				...prev,
				overdueChecklists: [...(prev.overdueChecklists || []), ...(response.result?.overdueChecklists || [])]
			}));

			setOverdueChecklistPage(nextPage);
		}

		setLoadingMoreOverdueChecklist(false);
	};

	const loadMoreCompletedChecklists = async () => {
		setLoadingMoreCompletedChecklist(true);

		const nextPage = completedChecklistPage + 1;

		const response = await apibridge.getStaffPast(staffId, overdueChecklistPage, nextPage);

		if (!response.isError && response.result) {
			setStaffPastProfileData((prev) => ({
				...prev,
				completedChecklists: [...(prev.completedChecklists || []), ...(response.result?.completedChecklists || [])]
			}));

			setCompletedChecklistPage(nextPage);
		}

		setLoadingMoreCompletedChecklist(false);
	};

	React.useEffect(() => {
		getStaffPastProfileData();
	}, []);

	const {
		id,
		childAwareId,
		profileImage,
		firstName,
		lastName,
		email,
		phoneNumber,
		dateOfBirth,
		staffType,
		isCurrentStaff,
		isOrganisationAdmin,
		certifications,
		organisations,
		complaints,
		feedback,
		incidents,
		policies,
		overdueChecklists,
		completedChecklists,
		trainings,
		referenceChecks,
		isYou,
		canUpdate
	} = staffPastProfileData;

	// Find the staff Role
	const staffRole = staffTypes?.find((type) => type.value === staffType)?.label;

	// Maximum number of items per section
	const defaultMaxItems: number = 4;

	return (
		<div className="page-general-layout staff-profile-page">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								{/* PROFILE SUMMARY */}
								<ProfileSummary
									id={id}
									profileImage={profileImage}
									firstName={firstName}
									lastName={lastName}
									childAwareId={childAwareId}
									email={email}
									phoneNumber={phoneNumber}
									dateOfBirth={dateOfBirth}
									canUpdate={isYou || false}
									isPastProfile
								/>

								{/* ACCOUNT INFO */}
								<section className="info-section">
									<div className="section-header">
										<h2 className="header-name color-dark">Account information</h2>
									</div>

									{childAwareId && (
										<div className="row-container">
											<div className="row detail-row">
												<div className="col-12 pdl-0">
													<div className="row detail-info">
														<div className="col-lg-4 pdl-0 detail-name bold">ChildAware ID</div>

														<div className="col-lg-8 pdl-0 detail-status account">{childAwareId}</div>
													</div>
												</div>
											</div>
										</div>
									)}

									{phoneNumber && (
										<div className="row-container">
											<div className="row detail-row">
												<div className="col-12 pdl-0">
													<div className="row detail-info">
														<div className="col-lg-4 pdl-0 detail-name bold">Phone</div>

														<div className="col-lg-8 pdl-0 detail-status account">{phoneNumber}</div>
													</div>
												</div>
											</div>
										</div>
									)}

									{email && (
										<div className="row-container">
											<div className="row detail-row">
												<div className="col-12 pdl-0">
													<div className="row detail-info">
														<div className="col-lg-4 pdl-0 detail-name bold">Email</div>

														<div className="col-lg-8 pdl-0 detail-status account">{email}</div>
													</div>
												</div>
											</div>
										</div>
									)}

									{dateOfBirth && (
										<div className="row-container">
											<div className="row detail-row">
												<div className="col-12 pdl-0">
													<div className="row detail-info">
														<div className="col-lg-4 pdl-0 detail-name bold">Date of Birth</div>

														<div className="col-lg-8 pdl-0 detail-status account">
															<DisplayDate>{dateOfBirth}</DisplayDate>
														</div>
													</div>
												</div>
											</div>
										</div>
									)}

									{staffRole && (
										<div className="row-container">
											<div className="row detail-row">
												<div className="col-12 pdl-0">
													<div className="row detail-info">
														<div className="col-lg-4 pdl-0 detail-name bold">Role</div>

														<div className="col-lg-8 pdl-0 detail-status account">{staffRole}</div>
													</div>
												</div>
											</div>
										</div>
									)}
								</section>

								{/* DOCUMENTS */}
								<PastProfileCertifications
									certifications={certifications}
									referenceChecks={referenceChecks}
									defaultMaxItems={defaultMaxItems}
									staffId={staffId}
									canUpdate={canUpdate}
									isYou={isYou}
									getStaffProfileData={getStaffPastProfileData}
								/>

								{/* ORGANISATIONS */}
								<ProfileOrganisations isYou={isYou} organisations={organisations} defaultMaxItems={defaultMaxItems} />

								{/* COMPLAINTS */}
								<ProfileComplaints complaints={complaints} defaultMaxItems={defaultMaxItems} isPastProfile />

								{/* FEEDBACK */}
								<ProfileFeedback feedback={feedback} defaultMaxItems={defaultMaxItems} isPastProfile />

								{/* INCIDENTS REPORT */}
								<ProfileIncidents incidents={incidents} defaultMaxItems={defaultMaxItems} isPastProfile />

								{/* POLICIES */}
								<ProfilePolicies policies={policies} defaultMaxItems={defaultMaxItems} isPastProfile />

								{/* CHECKLISTS*/}
								<ProfileChecklists
									overdueChecklists={overdueChecklists}
									completedChecklists={completedChecklists}
									overdueTotalCount={staffPastProfileData.overdueChecklistTotalCount}
									completedTotalCount={staffPastProfileData.completedChecklistTotalCount}
									onLoadMoreOverdue={loadMoreOverdueChecklists}
									onLoadMoreCompleted={loadMoreCompletedChecklists}
									loadingMoreOverdue={loadingMoreOverdueChecklist}
									loadingMoreCompleted={loadingMoreCompletedChecklist}
									defaultMaxItems={10}
									isPastProfile
								/>

								{/* TRAINING*/}
								<ProfileTraining isYou={isYou} training={trainings} defaultMaxItems={defaultMaxItems} isPastProfile />

								<ScrollToTopButton extraClass="mtop-30" />
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default PastStaffProfile;
